import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Crown, 
  TrendingUp, 
  TrendingDown,
  Heart,
  MessageCircle,
  Share2,
  Users,
  BarChart3,
  Loader2,
  Lock,
  MapPin
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#f59e0b'];

// Chart style constants (defined outside component to avoid re-creation)
const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px'
};

const StatsPage = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [followerHistory, setFollowerHistory] = useState([]);

  const [followerGain, setFollowerGain] = useState(0);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/user/stats');
      setStats(response.data);
      
      if (response.data.is_premium) {
        const { data } = await api.get('/user/stats/history', { params: { days: 30 } });
        setFollowerGain(data.gained);
        setFollowerHistory(data.history.map((d) => ({
          date: new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
          followers: d.followers
        })));
      }
    } catch (error) {
      toast.error('Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="min-h-screen tropic-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Non-premium users see locked view
  if (!stats?.is_premium) {
    return (
      <div className="min-h-screen tropic-bg">
        <header className="sticky top-0 z-40 glass border-b">
          <div className="container mx-auto max-w-4xl px-4 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl font-bold">Statistiques</h1>
            </div>
          </div>
        </header>

        <main className="container mx-auto max-w-4xl px-4 py-6">
          <Card className="text-center py-12">
            <CardContent>
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Lock className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Statistiques Premium</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Débloquez les statistiques avancées pour analyser votre audience et optimiser votre contenu.
              </p>
              
              {/* Basic stats preview */}
              <div className="grid grid-cols-3 gap-4 mb-8 max-w-sm mx-auto">
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats?.posts_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Posts</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats?.followers_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Followers</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats?.following_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Following</p>
                </div>
              </div>

              <Button onClick={() => navigate('/subscription')} className="gap-2">
                <Crown className="h-4 w-4" />
                Passer à Premium
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Prepare data for charts
  const categoryData = Object.entries(stats.posts_by_category || {}).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));

  const communeData = Object.entries(stats.posts_by_commune || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([name, value]) => ({ name, value }));

  const engagementRate = stats.engagement_rate || 0;
  const engagementTrend = engagementRate > 2 ? 'up' : engagementRate < 1 ? 'down' : 'stable';

  return (
    <div className="min-h-screen tropic-bg pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b">
        <div className="container mx-auto max-w-4xl px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold">Statistiques</h1>
                <p className="text-sm text-muted-foreground">Analyse de votre audience</p>
              </div>
            </div>
            <Badge className="bg-primary/20 text-primary gap-1">
              <Crown className="h-3 w-3" />
              Premium
            </Badge>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-4xl px-4 py-6 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Heart className="h-6 w-6 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{stats.total_likes || 0}</p>
              <p className="text-xs text-muted-foreground">Likes reçus</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <MessageCircle className="h-6 w-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{stats.total_comments || 0}</p>
              <p className="text-xs text-muted-foreground">Commentaires</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Share2 className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{stats.total_shares || 0}</p>
              <p className="text-xs text-muted-foreground">Partages</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{stats.followers_count || 0}</p>
              <p className="text-xs text-muted-foreground">Followers</p>
            </CardContent>
          </Card>
        </div>

        {/* Engagement Rate */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Taux d'engagement
              </CardTitle>
              <div className={`flex items-center gap-1 text-sm ${
                engagementTrend === 'up' ? 'text-green-500' : 
                engagementTrend === 'down' ? 'text-red-500' : 'text-muted-foreground'
              }`}>
                {engagementTrend === 'up' ? <TrendingUp className="h-4 w-4" /> : 
                 engagementTrend === 'down' ? <TrendingDown className="h-4 w-4" /> : null}
                {engagementRate.toFixed(2)}%
              </div>
            </div>
            <CardDescription>
              (Likes + Commentaires + Partages) / (Posts × Followers)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-3 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary to-primary/60 rounded-full transition-all"
                style={{ width: `${Math.min(engagementRate * 10, 100)}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {engagementRate > 3 ? 'Excellent ! Votre audience est très engagée.' :
               engagementRate > 1 ? 'Bon engagement. Continuez ainsi !' :
               'Conseil : Interagissez plus avec votre communauté.'}
            </p>
          </CardContent>
        </Card>

        {/* Followers Evolution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Évolution des followers
            </CardTitle>
            <CardDescription data-testid="followers-gain">
              30 derniers jours · {followerGain >= 0 ? '+' : ''}{followerGain} follower{Math.abs(followerGain) > 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={followerHistory} data-testid="followers-chart">
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="followers" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6, fill: 'hsl(var(--primary))' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Posts Distribution */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* By Category */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Posts par catégorie</CardTitle>
            </CardHeader>
            <CardContent>
              {categoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                      layout="horizontal" 
                      verticalAlign="bottom"
                      wrapperStyle={{ fontSize: '12px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-muted-foreground py-8">Aucun post</p>
              )}
            </CardContent>
          </Card>

          {/* By Commune */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Top communes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {communeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={communeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      tick={{ fontSize: 11 }}
                      width={80}
                    />
                    <Tooltip />
                    <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-muted-foreground py-8">Aucun post</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Best Post */}
        {stats.best_post && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">🏆 Meilleur post</CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="p-4 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                onClick={() => navigate(`/post/${stats.best_post.post_id}`)}
              >
                <h3 className="font-medium mb-2">{stats.best_post.title}</h3>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Heart className="h-4 w-4 text-red-500" />
                    {stats.best_post.likes}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="h-4 w-4 text-blue-500" />
                    {stats.best_post.comments}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default StatsPage;
